import { useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ChevronDown,
  Lock,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";

/* Public seller acquisition page (/seller).
 *
 * Anonymous surface: no auth, no account. The inquiry form posts to the same
 * public endpoint the external marketing site uses (POST /seller-contacts), and
 * every submission lands in Admin -> Seller Contacts.
 *
 * The payload keys are the marketing form's camelCase names, so both front
 * doors send an identical body — see app/schemas/seller_contact.py, which
 * aliases them onto the snake_case columns.
 */

const BUSINESS_AGE = ["Less than 2 years", "2–5 years", "5–10 years", "10+ years"];
const EMPLOYEES = ["1–5", "6–15", "16–50", "50+"];
const REVENUE = ["Under $500K", "$500K – $1M", "$1M – $5M", "$5M – $10M", "$10M+"];
const EBITDA = ["Under $100K", "$100K – $500K", "$500K – $1M", "$1M+", "Not sure"];
const REASONS = [
  "Retirement",
  "Ready for a new challenge",
  "Health reasons",
  "Partner buyout",
  "Relocating",
  "Other",
];

const WHY_BL = [
  {
    icon: ShieldCheck,
    title: "Concierge support, step by step",
    body: "Experienced advisors walk you through the process personally — from valuation through closing — so you're never guessing what's next.",
  },
  {
    icon: Lock,
    title: "Privacy until you're ready",
    body: "Your employees, customers, and competitors don't need to know your business is for sale. Your details stay confidential until you decide to move forward.",
  },
  {
    icon: TrendingUp,
    title: "A tax strategist on your side",
    body: "A dedicated tax strategist works alongside your deal team to help you structure the sale so you keep more of what you've built.",
  },
  {
    icon: BadgeCheck,
    title: "Zero dollar commission",
    body: "Other brokers and platforms charge around 10% of your sale price. Business Locating charges sellers $0 in commission.",
  },
];

const COMPARISON = [
  { label: "Commission", bl: "$0", broker: "Up to 10% of sale price" },
  { label: "Confidentiality", bl: "Maintained until you choose to close", broker: "Varies by broker" },
  { label: "Concierge support", bl: "Dedicated advisor, step by step", broker: "Varies by broker" },
  { label: "Tax strategy guidance", bl: "Included", broker: "Rarely included" },
];

const STEPS = [
  {
    n: "1",
    title: "Tell us about your business",
    body: "Share a few basics — industry, location, revenue, and why you're considering a sale.",
  },
  {
    n: "2",
    title: "We review and qualify",
    body: "Our M&A team confirms you're a fit for the platform — confidentially, with no obligation.",
  },
  {
    n: "3",
    title: "You get a dedicated advisor",
    body: "A concierge advisor calls to walk through your options and next steps.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "I didn't want my crew or my customers finding out before I was ready. Business Locating kept everything quiet until the deal was done.",
    who: "Owner, regional HVAC company",
  },
  {
    quote:
      "Every other broker I talked to wanted 10%. Paying nothing in commission changed the math on my whole exit.",
    who: "Founder, commercial landscaping business",
  },
];

const FAQS = [
  {
    q: "Is selling my business through Business Locating really confidential?",
    a: "Yes. Your listing and financial details stay private. We don't disclose your business name, and we control who sees your information until you're ready to move forward and close.",
  },
  {
    q: "What does it cost to sell through Business Locating?",
    a: "Zero dollar commission. Traditional brokers typically charge around 10% of your sale price; Business Locating does not charge sellers a commission.",
  },
  {
    q: "What types of service businesses do you work with?",
    a: "We work with owners of established, service-based businesses across most industries, including home services, commercial services, IT and managed services, and professional services firms.",
  },
  {
    q: "How long does the process take?",
    a: "It varies by business, deal size, and market conditions. Your concierge advisor will walk you through a realistic timeline after reviewing your inquiry.",
  },
  {
    q: "What happens after I submit the inquiry form?",
    a: "Our M&A team reviews your information to confirm you qualify, then a dedicated advisor reaches out to schedule a confidential call.",
  },
];

/* Field chrome mirrored from <Input> so the native selects don't read as a
   different control set — there is no Select primitive in components/ui. */
const SELECT_CLASS =
  "flex h-11 w-full appearance-none rounded-2xl border border-border/80 bg-card/85 px-4 py-2.5 text-sm transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 focus-visible:border-primary/50 hover:border-primary/20 hover:bg-card";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground";

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={LABEL_CLASS}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  id,
  name,
  options,
  required = true,
}: {
  label: string;
  id: string;
  name: string;
  options: string[];
  required?: boolean;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <div className="relative">
        <select id={id} name={name} required={required} defaultValue="" className={SELECT_CLASS}>
          <option value="">Select one</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    </Field>
  );
}

export default function SellerLandingPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      businessType: String(fd.get("businessType") ?? ""),
      location: String(fd.get("location") ?? ""),
      businessAge: String(fd.get("businessAge") ?? ""),
      employees: String(fd.get("employees") ?? ""),
      revenue: String(fd.get("revenue") ?? ""),
      ebitda: String(fd.get("ebitda") ?? ""),
      reason: String(fd.get("reason") ?? ""),
      source: "seller_landing",
    };

    setSubmitting(true);
    try {
      await api.post("/seller-contacts", payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.status === 429
          ? "Too many submissions from this network. Please try again in a minute."
          : "Something went wrong sending your inquiry. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="public-stage">
      {/* ── Hero — pinned Dark · Gold, matching the buyer landing page ───── */}
      <section
        data-theme="dark"
        style={{ backgroundColor: "transparent" }}
        className="px-4 pt-6 sm:px-6 lg:px-8"
      >
        <div className="public-hero-shell relative w-full overflow-hidden rounded-[1.85rem] px-6 py-12 shadow-glow sm:px-8 sm:py-16">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-20 -top-16 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

          <div className="relative z-10 w-full">
            <div className="public-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              For Service-Based Business Owners
            </div>

            <h1 className="mt-6 max-w-4xl font-display text-4xl leading-[1.08] text-foreground sm:text-5xl lg:text-6xl">
              Sell your service business —{" "}
              <span className="text-gradient">without paying a 10% broker fee.</span>
            </h1>

            <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
              Business Locating connects service-business owners with a dedicated M&amp;A team,
              concierge support from day one, and complete confidentiality until you're ready to
              close. Zero dollar commission.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a href="#inquiry-form">
                <Button size="lg" className="w-full shadow-glow sm:w-auto">
                  See if you qualify
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
              <a href="#why-bl">
                <Button size="lg" variant="outline" className="w-full border-primary/25 sm:w-auto">
                  See why sellers choose us
                </Button>
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-2.5">
              {["$0 Commission", "100% Confidential Process", "Dedicated Concierge Team", "Tax Strategy Included"].map(
                (t) => (
                  <span key={t} className="public-pill border-primary/25 text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {t}
                  </span>
                ),
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Business Locating ────────────────────────────────────────── */}
      <section id="why-bl" className="mt-14 scroll-mt-20 px-4 sm:px-6 lg:px-8">
        <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
          Why Business Locating
        </div>
        <h2 className="mt-2.5 max-w-2xl font-display text-3xl text-foreground sm:text-4xl">
          The right M&amp;A platform for service business owners
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground sm:text-base">
          Selling a business you built is personal. We designed Business Locating around what owners
          actually need: a team that knows your side of the deal, privacy while you decide, and a
          structure that doesn't take a cut of your outcome.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_BL.map(({ icon: Icon, title, body }) => (
            <div key={title} className="public-glass-card rounded-2xl p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </span>
              <h3 className="mt-4 font-display text-lg font-bold leading-snug text-foreground">{title}</h3>
              <p className="mt-2 text-[0.85rem] leading-6 text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Comparison ───────────────────────────────────────────────────── */}
      <section className="mt-14 border-y border-border/60 bg-card/40 px-4 py-14 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl text-foreground sm:text-4xl">
          Business Locating vs. a traditional broker
        </h2>
        <div className="mt-7 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground" />
                <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-primary">
                  Business Locating
                </th>
                <th className="py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Traditional broker
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {COMPARISON.map((row) => (
                <tr key={row.label}>
                  <td className="py-3.5 pr-4 font-medium text-foreground">{row.label}</td>
                  <td className="py-3.5 pr-4 text-foreground/90">
                    {row.bl === "$0" ? (
                      <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                        $0
                      </span>
                    ) : (
                      row.bl
                    )}
                  </td>
                  <td className="py-3.5 text-muted-foreground">{row.broker}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section className="mt-14 px-4 sm:px-6 lg:px-8">
        <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">How It Works</div>
        <h2 className="mt-2.5 font-display text-3xl text-foreground sm:text-4xl">
          From inquiry to a confidential call
        </h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n}>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 font-display text-base font-bold text-primary">
                {s.n}
              </span>
              <h3 className="mt-4 font-display text-lg font-bold text-foreground">{s.title}</h3>
              <p className="mt-1.5 text-[0.85rem] leading-6 text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <section className="mt-14 px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl text-foreground sm:text-4xl">What sellers say</h2>
        <div className="mt-7 grid gap-5 md:grid-cols-2">
          {TESTIMONIALS.map((t) => (
            <div key={t.who} className="public-glass-card rounded-2xl p-6">
              <p className="text-[0.95rem] italic leading-7 text-foreground/90">“{t.quote}”</p>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                — {t.who}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Inquiry form ─────────────────────────────────────────────────── */}
      <section
        id="inquiry-form"
        className="mt-14 scroll-mt-20 border-y border-border/60 bg-card/40 px-4 py-14 sm:px-6 lg:px-8"
      >
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
              Confidential Inquiry
            </div>
            <h2 className="mt-2.5 font-display text-3xl text-foreground sm:text-4xl">
              See if you qualify for a call with our M&amp;A team
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-7 text-muted-foreground">
              Tell us a bit about your business. A dedicated BL advisor will review your inquiry and
              follow up to qualify you for a confidential call — no commitment, no obligation.
            </p>
            <ul className="mt-5 space-y-2.5">
              {[
                "Reviewed by a real advisor, not a bot",
                "Your details stay confidential",
                "No cost, no obligation to proceed",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div>
            {submitted ? (
              <div className="public-glass-card rounded-2xl px-6 py-12 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </span>
                <h3 className="mt-5 font-display text-2xl text-foreground">You're in the queue</h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-muted-foreground">
                  A dedicated BL advisor will review your inquiry and reach out shortly to schedule
                  your confidential call.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="public-glass-card flex flex-col gap-4 rounded-2xl p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="f-name">
                    <Input id="f-name" name="name" type="text" required maxLength={150} autoComplete="name" />
                  </Field>
                  <Field label="Email" htmlFor="f-email">
                    <Input id="f-email" name="email" type="email" required autoComplete="email" />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Phone" htmlFor="f-phone">
                    <Input id="f-phone" name="phone" type="tel" required maxLength={50} autoComplete="tel" />
                  </Field>
                  <Field label="Type of business" htmlFor="f-type">
                    <Input
                      id="f-type"
                      name="businessType"
                      type="text"
                      required
                      maxLength={150}
                      placeholder="e.g. HVAC, landscaping, IT services"
                    />
                  </Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Business location" htmlFor="f-location">
                    <Input
                      id="f-location"
                      name="location"
                      type="text"
                      required
                      maxLength={150}
                      placeholder="City, State"
                    />
                  </Field>
                  <SelectField label="Years in business" id="f-age" name="businessAge" options={BUSINESS_AGE} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <SelectField label="Number of employees" id="f-employees" name="employees" options={EMPLOYEES} />
                  <SelectField label="Annual revenue" id="f-revenue" name="revenue" options={REVENUE} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <SelectField
                    label="EBITDA (annual)"
                    id="f-ebitda"
                    name="ebitda"
                    options={EBITDA}
                    required={false}
                  />
                  <SelectField label="Reason for sale" id="f-reason" name="reason" options={REASONS} />
                </div>

                <Button type="submit" size="lg" disabled={submitting} className="mt-1 w-full shadow-glow">
                  {submitting ? "Sending…" : "See if you qualify"}
                  {!submitting && <ArrowRight className="h-4 w-4" />}
                </Button>
                <p className="text-[0.7rem] leading-5 text-muted-foreground">
                  By submitting, you agree to be contacted by a Business Locating advisor about your
                  inquiry. Your information is kept confidential.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="mt-14 scroll-mt-20 px-4 pb-14 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl text-foreground sm:text-4xl">Common questions from sellers</h2>
        <div className="mt-7 divide-y divide-border/50 border-y border-border/50">
          {FAQS.map(({ q, a }) => (
            <details key={q} className="group py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-base font-bold text-foreground">
                {q}
                <ChevronDown className="h-4 w-4 shrink-0 text-primary transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-2.5 max-w-3xl text-sm leading-7 text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-primary/25 bg-primary/[0.06] px-6 py-10 text-center">
          <h3 className="font-display text-2xl text-foreground">
            Ready to see what your business could sell for?
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-muted-foreground">
            Submit a confidential inquiry and a dedicated advisor will follow up — no cost, no
            obligation.
          </p>
          <a href="#inquiry-form" className="mt-6 inline-block">
            <Button size="lg" className="shadow-glow">
              See if you qualify
              <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
